import { PlusOutlined } from "@ant-design/icons"
import { Button, Checkbox, Form } from "antd"
import dayjs from "dayjs"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useParams, useSearchParams } from "react-router-dom"
import { modelSelector } from "store/selectors"
import { updatedSeasonal } from "store/trips"
import { Card } from "ui/Card"
import { DateRangeBlock } from "../components/scheduling/DateRangeBlock/DateRangeBlock"
import { SeasonTypeSelector } from "../components/scheduling/SeasonTypeSelector/SeasonTypeSelector"
import { BlackOutDates } from "../components/scheduling/BlackOutDates/BlackOutDates"
import { DurationAndCutOff } from "../components/scheduling/DurationAndCutOff/DurationAndCutOff"
import { AvailabilityDisplay } from "../components/scheduling/AvailabilityDisplay/AvailabilityDisplay"
import { TicketedSchedule } from "../components/scheduling/TicketedSchedule/TicketedSchedule"
import { ExperienceDurationType } from "../components/scheduling/ExperienceDurationType/ExperienceDurationType"
import { PrivateCutOff } from "../components/scheduling/PrivateCutOff/PrivateCutOff"
import { AvailabilitySchedule } from "../components/scheduling/AvailabilitySchedule/AvailabilitySchedule"
import { SEASON_TYPES } from "pagesV2/pro/Experiences/ListingScheduling/utils/constants"
import { buildSchedulingPayload } from "../mappers/toApiPayload"
import { useWizardContext } from "../WizardContext"

export const SchedulingStep = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const { form, stepSaveHandlersRef, stepValidateHandlersRef } = useWizardContext()
  const listingType = searchParams.get("type")
  const isTicketed = listingType === "ticketed"
  const trip = useSelector(modelSelector("trip"))
  const hasInitialized = useRef(false)

  // React state is the source of truth; form is kept in sync for payload building and persistence
  const [isSeasonalExperience, setIsSeasonalExperience] = useState(
    () => form.getFieldValue("isSeasonalExperience") ?? false
  )
  const [timeBlocks, setTimeBlocks] = useState(
    () => form.getFieldValue("timeBlocks") || []
  )

  // Re-sync when WizardProvider loads existing trip data after this component mounts
  useEffect(() => {
    if (!trip?.id || hasInitialized.current) return

    const isSeasonal = trip.seasonal === true
    setIsSeasonalExperience(isSeasonal)
    form.setFieldValue("isSeasonalExperience", isSeasonal)

    if (isSeasonal && trip?.available_date_ranges?.length > 0) {
      const rangeValues = {}
      trip.available_date_ranges.forEach((range, index) => {
        if (range.start) rangeValues[`seasonStart_${index}`] = dayjs(range.start)
        if (range.end) rangeValues[`seasonEnd_${index}`] = dayjs(range.end)
      })
      form.setFieldsValue(rangeValues)
      const blocks = trip.available_date_ranges.map((_, i) => i)
      setTimeBlocks(blocks)
      form.setFieldValue("timeBlocks", blocks)
    } else if (isSeasonal) {
      setTimeBlocks([0])
      form.setFieldValue("timeBlocks", [0])
    }

    hasInitialized.current = true
  }, [form, trip])

  useEffect(() => {
    hasInitialized.current = false
  }, [id])

  const handleSeasonChange = (e) => {
    const isSeasonal = e.target.value === SEASON_TYPES.SEASONAL
    setIsSeasonalExperience(isSeasonal)
    form.setFieldValue("isSeasonalExperience", isSeasonal)
    if (isSeasonal && timeBlocks.length === 0) {
      setTimeBlocks([0])
      form.setFieldValue("timeBlocks", [0])
    }
    form.setFieldsValue({ seasonal: isSeasonal })
    dispatch(updatedSeasonal(isSeasonal))
  }

  const handleAddTimeBlock = () => {
    setTimeBlocks((prev) => {
      const next = [...prev, prev.length ? Math.max(...prev) + 1 : 0]
      form.setFieldValue("timeBlocks", next)
      return next
    })
  }

  const handleRemoveTimeBlock = (index) => {
    setTimeBlocks((prev) => {
      const next = prev.filter((i) => i !== index)
      form.setFieldValue("timeBlocks", next)
      return next
    })
    form.setFieldsValue({ [`seasonStart_${index}`]: undefined, [`seasonEnd_${index}`]: undefined })
  }

  const getAllSelectedDateRanges = useMemo(
    () =>
      timeBlocks
        .map((blockIndex) => {
          const start = form.getFieldValue(`seasonStart_${blockIndex}`)
          const end = form.getFieldValue(`seasonEnd_${blockIndex}`)
          return start && end ? { blockIndex, start, end } : null
        })
        .filter(Boolean),
    [form, timeBlocks]
  )

  useEffect(() => {
    stepSaveHandlersRef.current["scheduling"] = async () => {
      const values = form.getFieldsValue()
      // time_slots is set via setFieldValue (no Form.Item), so getFieldsValues() omits it — read directly
      const timeSlots = form.getFieldValue("time_slots") || []
      return buildSchedulingPayload({ ...values, time_slots: timeSlots }, { isSeasonalExperience, timeBlocks, isTicketed })
    }
    return () => {
      delete stepSaveHandlersRef.current["scheduling"]
    }
  }, [form, stepSaveHandlersRef, isSeasonalExperience, timeBlocks, isTicketed])

  useEffect(() => {
    if (!isTicketed) return
    stepValidateHandlersRef.current["scheduling"] = async () => {
      const timeSlots = form.getFieldValue("time_slots") || []
      if (timeSlots.length === 0) {
        const err = new Error("At least one scheduled slot is required")
        err.errorFields = [{ errors: ["At least one scheduled slot is required"] }]
        throw err
      }
    }
    return () => {
      delete stepValidateHandlersRef.current["scheduling"]
    }
  }, [form, stepValidateHandlersRef, isTicketed])

  return (
    <div>
      {isTicketed && <DurationAndCutOff />}
      {isTicketed && <TicketedSchedule />}
      {isTicketed && <AvailabilityDisplay />}

      {!isTicketed && (
        <>
          {/* Seasonality */}
          <Card className="mb-3xl" title={t("listings.tabs.scheduling.sections.seasons.title")}>
            <SeasonTypeSelector onChange={handleSeasonChange} value={isSeasonalExperience ? SEASON_TYPES.SEASONAL : SEASON_TYPES.ALL_YEAR} />
            {isSeasonalExperience && (
              <>
                {timeBlocks.map((blockIndex) => {
                  const dateRange = trip?.available_date_ranges?.[blockIndex]
                  return (
                    <DateRangeBlock
                      key={blockIndex}
                      blockIndex={blockIndex}
                      onDelete={handleRemoveTimeBlock}
                      isSeasonalActive={isSeasonalExperience}
                      initialStartDate={dateRange?.start ? dayjs(dateRange.start) : undefined}
                      initialEndDate={dateRange?.end ? dayjs(dateRange.end) : undefined}
                      allRanges={getAllSelectedDateRanges}
                      timeBlocks={timeBlocks}
                    />
                  )
                })}
                <Button type="link" icon={<PlusOutlined />} onClick={handleAddTimeBlock} className="mt-m pl-0">
                  {t("listings.tabs.scheduling.sections.seasons.add_time_block")}
                </Button>
                <Form.Item name="recurringYearly" valuePropName="checked" className="mt-l">
                  <Checkbox>{t("listings.tabs.scheduling.sections.seasons.recurring_yearly")}</Checkbox>
                </Form.Item>
              </>
            )}
          </Card>

          {/* Duration type — Multi-Day flow deferred */}
          <ExperienceDurationType />

          {/* Cut-off */}
          <PrivateCutOff />

          {/* Availability Schedule (replaces DaysAndHours) */}
          <AvailabilitySchedule form={form} />

          {/* Blackout Dates — static display, data managed globally */}
          <BlackOutDates />
        </>
      )}
    </div>
  )
}
